import React, { useState } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Fieldset } from 'primereact/fieldset'

import {
  Btn,
  CustomCheckbox,
  CustomDataTableHeader,
  CustomPanel,
  CustomMultiSelect,
  Loading,
  NumeroDecimal,
  PageHeader,
  Periodo,
  SelectEmpresas,
} from '../../../../../components'

import { tableCellCentered } from '../../../../../assets/js/styles'
import { showMessageBox } from '../../../../../store/actions/system.actions'
import { errorHandler } from '../../../../../services/util/error.service'
import ModalEmitirCteGlobalizado from './ModalEmitirCteGlobalizado'
import {
  getDeliveriesForCteGlobalizado,
  calcularFreteMultiplo,
} from '../../../../../services/api/https/deliveries/cte.http'
import { getPeriodoMesAtual } from '../../../../../services/util/date.service'
import { getEmpresas } from '../../../../../services/localstorage/localstorage.service'
import Badge from '../../../../../components/Badge/Badge'
import * as colors from '../../../../../assets/js/colors'

function EmissaoCteGlobalizado({ dispatch, selectedTp, history }) {
  // Hooks
  const [entregas, setEntregas] = useState([])
  const [entregasOriginal, setEntregasOriginal] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [modalCte, setModalCte] = useState({ show: false, valorFrete: null, componentes: null })
  const [selectedEmpresa, setSelectedEmpresa] = useState(getEmpresas()[0].id || '')
  const [periodo, setPeriodo] = useState(getPeriodoMesAtual())
  const [globalFilter, setGlobalFilter] = useState('')
  const [selecionarTodos, setSelecionarTodos] = useState(false)

  // Estados para filtros
  const [ufsOptions, setUfsOptions] = useState([])
  const [selectedUfs, setSelectedUfs] = useState([])
  const [citiesOptions, setCitiesOptions] = useState([])
  const [selectedCities, setSelectedCities] = useState([])
  const [selectedCteEmitido, setSelectedCteEmitido] = useState([])

  // Events
  const onPesquisar = async (pInicial, pFinal) => {
    try {
      if (!selectedTp) throw new Error('Selecione o Operador Logístico desejado')
      if (!selectedEmpresa) throw new Error('Selecione a Empresa Desejada')
      setIsLoading(true)
      setPeriodo({ dataInicial: pInicial, dataFinal: pFinal })

      const fetchedEntregas = await getDeliveriesForCteGlobalizado(
        selectedTp,
        selectedEmpresa,
        pInicial,
        pFinal,
      )

      const entregasWithSelection = fetchedEntregas.map(e => ({ ...e, selected: false }))
      setEntregasOriginal(entregasWithSelection)
      setEntregas(entregasWithSelection)
      setSelecionarTodos(false)

      // Gerar opções para filtros
      generateFilterOptions(entregasWithSelection)
    } catch (e) {
      dispatch(showMessageBox(null, errorHandler(e)))
    } finally {
      setIsLoading(false)
    }
  }

  const generateFilterOptions = entregasData => {
    // Gerar opções de UF
    const ufsUnicas = [...new Set(entregasData.map(e => e.uf))]
    setUfsOptions(ufsUnicas.map(uf => ({ label: uf, value: uf })))

    // Gerar opções de cidade
    const cidadesUnicas = [...new Set(entregasData.map(e => e.city))]
    setCitiesOptions(cidadesUnicas.map(city => ({ label: city, value: city })))
  }

  const applyFilters = () => {
    let filteredEntregas = [...entregasOriginal]

    // Filtro por UF
    if (selectedUfs.length > 0) {
      filteredEntregas = filteredEntregas.filter(e => selectedUfs.includes(e.uf))
    }

    // Filtro por cidade
    if (selectedCities.length > 0) {
      filteredEntregas = filteredEntregas.filter(e => selectedCities.includes(e.city))
    }

    // Filtro por CTe emitido
    if (selectedCteEmitido.length > 0) {
      filteredEntregas = filteredEntregas.filter(e => {
        const isEmitido = e.cte_status === 'autorizado'

        if (selectedCteEmitido.includes('Emitido') && selectedCteEmitido.includes('Não Emitido')) {
          return true // Mostra todos se ambos estão selecionados
        } else if (selectedCteEmitido.includes('Emitido')) {
          return isEmitido
        } else if (selectedCteEmitido.includes('Não Emitido')) {
          return !isEmitido
        }
        return false
      })
    }

    setEntregas(filteredEntregas)
  }

  // Aplicar filtros sempre que os valores mudarem
  React.useEffect(() => {
    if (entregasOriginal.length > 0) {
      applyFilters()
    }
  }, [selectedUfs, selectedCities, selectedCteEmitido, entregasOriginal])

  const onError = error => {
    dispatch(showMessageBox(null, errorHandler(error)))
  }

  const onSelecionarTodosChange = checked => {
    setSelecionarTodos(checked)
    setEntregas(prevEntregas => prevEntregas.map(e => ({ ...e, selected: checked })))
  }

  const onCheckboxChange = (delivery, checked) => {
    setEntregas(prevEntregas =>
      prevEntregas.map(e =>
        e.delivery_id === delivery.delivery_id ? { ...e, selected: checked } : e,
      ),
    )
  }

  const onEmitirCteClick = async () => {
    try {
      const selectedEntregas = entregas.filter(e => e.selected)

      if (selectedEntregas.length < 5) {
        throw new Error('Um CTe Globalizado deve conter no mínimo 5 destinatários')
      }

      if (selectedEntregas.length > 100) {
        throw new Error('Um CTe Globalizado deve conter no máximo 100 destinatários')
      }

      // Verificar se alguma entrega já tem CTe emitido
      const entregasComCte = selectedEntregas.filter(e => e.cte_status === 'autorizado')
      if (entregasComCte.length > 0) {
        throw new Error(
          `As seguintes entregas já possuem CTe emitido: ${entregasComCte.map(e => e.identifier).join(', ')}`,
        )
      }

      setIsLoading(true)

      // Calcular frete total na API
      const deliveriesIds = selectedEntregas.map(e => e.delivery_id)
      const response = await calcularFreteMultiplo(deliveriesIds)

      setModalCte({ show: true, valorFrete: response.frete, componentes: response.componentes })
    } catch (e) {
      dispatch(showMessageBox(null, errorHandler(e)))
    } finally {
      setIsLoading(false)
    }
  }

  const getSelectedDeliveriesIds = () => {
    return entregas.filter(e => e.selected).map(e => e.delivery_id)
  }

  // Render
  return (
    <>
      <PageHeader>3.22 - Emissão CTe Globalizado</PageHeader>
      <div style={{ marginTop: 20 }}>
        <CustomPanel showBtnNovo={false} onVoltar={history.goBack}>
          <div className="p-grid" style={{ marginLeft: 0 }}>
            <div className="p-col-3">
              <SelectEmpresas
                onChange={empresa => setSelectedEmpresa(empresa.id)}
                companyId={selectedEmpresa}
              />
            </div>
            <div className="p-col-6" style={{ display: 'flex' }}>
              <Periodo
                periodoFinal={periodo.dataFinal}
                periodoInicial={periodo.dataInicial}
                onPesquisar={onPesquisar}
              />
            </div>
          </div>

          {entregas.length > 0 && (
            <>
              <Fieldset
                legend="Filtro"
                toggleable
                collapsed={true}
                className="p-col-12"
                style={{ marginTop: 20 }}
              >
                <div style={{ display: 'flex', gap: 16 }}>
                  <div className="p-col-2" style={{ minWidth: 200 }}>
                    <CustomMultiSelect
                      marginTop={10}
                      label="Estados"
                      options={ufsOptions}
                      value={selectedUfs}
                      onChange={setSelectedUfs}
                    />
                  </div>
                  <div className="p-col-2" style={{ minWidth: 200 }}>
                    <CustomMultiSelect
                      marginTop={10}
                      label="Cidades"
                      options={citiesOptions}
                      value={selectedCities}
                      onChange={setSelectedCities}
                    />
                  </div>
                  <div className="p-col-2" style={{ minWidth: 200 }}>
                    <CustomMultiSelect
                      marginTop={10}
                      label="CTe emitido"
                      options={[
                        { label: 'Emitido', value: 'Emitido' },
                        { label: 'Não Emitido', value: 'Não Emitido' },
                      ]}
                      value={selectedCteEmitido}
                      onChange={setSelectedCteEmitido}
                    />
                  </div>
                </div>
              </Fieldset>

              <div
                className="p-col-12"
                style={{
                  marginTop: 15,
                  marginBottom: 10,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <CustomCheckbox
                  checked={selecionarTodos}
                  onChange={value => onSelecionarTodosChange(value)}
                >
                  Selecionar Todos
                </CustomCheckbox>
                <Btn onClick={onEmitirCteClick} disabled={isLoading} style={{ marginLeft: '30px' }}>
                  Emitir CTe
                </Btn>
              </div>
            </>
          )}

          <div className="p-col-12">
            {isLoading ? (
              <div className="text-center" style={{ marginTop: 20 }}>
                <Loading />
              </div>
            ) : (
              <DataTableComponent
                entregas={entregas}
                globalFilter={globalFilter}
                setGlobalFilter={setGlobalFilter}
                onCheckboxChange={onCheckboxChange}
              />
            )}
          </div>
        </CustomPanel>

        {modalCte.show && (
          <ModalEmitirCteGlobalizado
            selectedTp={selectedTp}
            visible={modalCte.show}
            valorFrete={modalCte.valorFrete}
            componentes={modalCte.componentes}
            deliveriesIds={getSelectedDeliveriesIds()}
            onModalClose={() => {
              setModalCte({ show: false, valorFrete: null, componentes: null })
            }}
            onModalSave={() => {
              // Marcar entregas como CTe emitido e desselecionar
              const selectedIds = getSelectedDeliveriesIds()
              setEntregas(prevState =>
                prevState.map(entrega =>
                  selectedIds.includes(entrega.delivery_id)
                    ? { ...entrega, cte_status: 'autorizado', selected: false }
                    : entrega,
                ),
              )
              dispatch(
                showMessageBox(
                  'CTe Globalizado enviado para emissão, aguardando resposta SEFAZ',
                  null,
                ),
              )
              setModalCte({ show: false, valorFrete: null, componentes: null })
            }}
            onError={onError}
          />
        )}
      </div>
    </>
  )
}

function DataTableComponent({ entregas, globalFilter, setGlobalFilter, onCheckboxChange }) {
  return (
    <DataTable
      value={entregas}
      paginator={true}
      rows={20}
      emptyMessage="Não existem entregas cadastradas"
      globalFilter={globalFilter}
      header={<CustomDataTableHeader onChange={setGlobalFilter} />}
    >
      <Column
        header={'#'}
        body={item => (
          <CustomCheckbox
            checked={item.selected}
            onChange={value => onCheckboxChange(item, value)}
          />
        )}
        style={tableCellCentered(50)}
      />
      <Column field="identifier" header="Identificador" />
      <Column field="invoice_serie" header="Série da NF" style={tableCellCentered()} />
      <Column field="nomeFantasia" header="Nome Fantasia" />
      <Column field="uf" header="UF" style={tableCellCentered(60)} />
      <Column field="city" header="Cidade" />
      <Column
        header="Valor do Frete"
        body={item =>
          item.valor_frete !== null ? (
            <NumeroDecimal>{item.valor_frete}</NumeroDecimal>
          ) : (
            <span style={{ color: 'gray' }}>-</span>
          )
        }
        style={tableCellCentered()}
        sortField="valor_frete"
      />
      <Column
        header="Valor da NF"
        body={item => <NumeroDecimal>{item.valor_nf}</NumeroDecimal>}
        style={tableCellCentered()}
        sortField="valor_nf"
      />
      <Column field="qteSkuTotal" header="Total Caixas" style={tableCellCentered()} />
      <Column
        style={tableCellCentered(130)}
        header="CTe emitido"
        body={entrega => {
          const getColorAndText = () => {
            if (entrega.cte_status === 'autorizado') return { color: colors.verde, text: 'Emitido' }
            if (entrega.cte_status === 'processando_autorizacao')
              return { color: colors.laranja, text: 'Processando Emissão' }
            return { color: colors.vermelho, text: 'Não Emitido' }
          }
          const { color, text } = getColorAndText()
          return <Badge color={color}>{text}</Badge>
        }}
      />
    </DataTable>
  )
}

function mapStateToProps({ system: { selectedTp } }) {
  return {
    selectedTp,
  }
}

export default connect(mapStateToProps)(withRouter(EmissaoCteGlobalizado))
